import { useMyRoleSupportEmailQuery } from 'generated/graphql';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

/**
 * Spotted Zebra's default candidate support address. Shown until the current
 * role's company is known, and for every company that has not switched on
 * "Enable alternative support email address" (CR-473).
 */
export const DEFAULT_SUPPORT_EMAIL = 'candidate@spottedzebra.co.uk';

export type TSupportEmail = {
  /** The address to show the candidate. */
  supportEmail: string;
  /** `mailto:` link for {@link supportEmail}. */
  supportEmailHref: string;
};

const toSupportEmailValue = (supportEmail: string): TSupportEmail => ({
  supportEmail,
  supportEmailHref: `mailto:${supportEmail}`,
});

const SupportEmailContext = createContext<TSupportEmail>(
  toSupportEmailValue(DEFAULT_SUPPORT_EMAIL)
);

/**
 * The role whose company decides the support address. Every candidate flow
 * lives under `/roles/:roleId`.
 */
const useCurrentRoleId = (): string | undefined =>
  useMatch({ path: '/roles/:roleId', end: false })?.params.roleId;

/**
 * Resolves the support address once per role (Apollo caches the query for the
 * session) so screens can read it with {@link useSupportEmail} instead of
 * threading it through props. Falls back to the default address while the
 * query is in flight, when there is no current role, and on error.
 */
export const SupportEmailProvider = ({ children }: { children: ReactNode }) => {
  const roleId = useCurrentRoleId();

  const { data } = useMyRoleSupportEmailQuery({
    variables: { roleId: roleId as string },
    skip: !roleId,
  });

  const supportEmail =
    data?.myRole?.company?.supportEmail || DEFAULT_SUPPORT_EMAIL;

  const value = useMemo(() => toSupportEmailValue(supportEmail), [supportEmail]);

  return (
    <SupportEmailContext.Provider value={value}>
      {children}
    </SupportEmailContext.Provider>
  );
};

/**
 * The address candidates are told to contact for support, for the role they
 * are currently in. Outside a {@link SupportEmailProvider} it is the default
 * address, which keeps component tests free of GraphQL mocks.
 */
export const useSupportEmail = (): TSupportEmail =>
  useContext(SupportEmailContext);
